import {
  Button,
  css,
  EnterIcon,
  FormField,
  ListItem,
  Paragraph,
  PlusIcon,
  SelectInput,
  Stack,
  styled,
  TextInput,
} from 'canoe';
import { Field, FieldInputProps, FieldMetaProps, Form, Formik } from 'formik';
import { createCacheKey, ENDPOINTS, fetchResource, useActions, useCreatePayload } from 'hammock';
import { debounce } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import * as Yup from 'yup';

import { ContentType } from '@candu-ai/content/types';

import { namePlaceholderTxt } from '../utils';

export const validationSchema = Yup.object().shape({
  contentName: Yup.string()
    .min(3, 'Must have at least 3 characters')
    .max(255, 'Cannot be longer then 255 characters')
    .required('You must specify a Content name'),
});

const NewItem = styled.div<{ selected?: boolean }>`
  ${({ theme, selected }) => css`
    display: flex;
    border-bottom: 1px solid ${theme.colors.GlitterGray};
    padding: ${theme.spaces.xxs} ${theme.spaces.xs};
    line-height: 28px;

    svg {
      padding-right: ${theme.spaces.xxs};
    }

    ${selected &&
    `
      align-items: center;
      background-color: ${theme.colors.GlitterGrayL10};
      border-bottom: 1px solid ${theme.colors.GlitterGrayD10};
      color: ${theme.colors.Black};
      font-weight: ${theme.fontWeights.medium};
      justify-content: space-between;
    `}
  `}
`;

const EnterContainer = styled(Paragraph)`
  ${({ theme }) => css`
    display: flex;
    font-size: ${theme.fonts.small.fontSize};

    svg {
      padding-left: ${theme.spaces.xxs};
    }
  `}
`;

const renderSectionItem = (itemProps: any) => {
  if (itemProps.searching) {
    return (
      <NewItem selected>
        {itemProps.label}
        <EnterContainer color="OnyxGrayL30" mb="none">
          Press Enter to save <EnterIcon color="OnyxGrayL30" />
        </EnterContainer>
      </NewItem>
    );
  }

  if (!itemProps.value) {
    return (
      <NewItem>
        <PlusIcon /> {itemProps.label}
      </NewItem>
    );
  }

  return <ListItem {...itemProps}>{itemProps.label}</ListItem>;
};

type Item = { label: string; value: number; searching?: boolean };
type Data = { name: string; id: number; searching?: boolean };

const getFormattedResults = (data: Data[]) => {
  return data.map((item) => ({
    label: item.name,
    value: item.id,
    searching: item.searching,
  }));
};

interface TutorialMetadataFromProps {
  contentType: ContentType;
  onSubmit: (payload: object) => void;
  withSnapshots?: boolean;
  folderId?: string;
  contentTitle?: string;
}

export const ContentMetadataForm = ({
  onSubmit,
  withSnapshots,
  contentType,
  folderId,
  contentTitle,
}: TutorialMetadataFromProps) => {
  const { getAll } = useCreatePayload(ENDPOINTS.contentFolder);
  const { create } = useActions(ENDPOINTS.contentFolder) as any;
  const [searchValue, setSearchValue] = useState('');
  const [loading, setLoading] = useState(false);
  const payload = getAll({});

  const { data: results, isFetching } = useQuery(createCacheKey(payload), () =>
    fetchResource<Data[]>(payload),
  );

  // react-query seems to re-use references for results so including isFetching in deps forces a refresh
  const sectionOptions: Item[] = useMemo(() => {
    if (!results) {
      return [];
    }
    const alreadyExists = results.find(
      (item) => item.name.toLowerCase() === searchValue.toLowerCase(),
    );

    const formattedResults = getFormattedResults(results);

    if (alreadyExists) {
      return formattedResults.filter((item) =>
        item.label.toLowerCase().includes(searchValue?.toLowerCase()),
      );
    }

    return [
      ...[
        {
          value: 0,
          label: searchValue || 'Start typing to create a new Section',
          searching: Boolean(searchValue),
        },
      ],
      ...formattedResults.filter((item) =>
        item.label.toLowerCase().includes(searchValue?.toLowerCase()),
      ),
    ];
  }, [results, isFetching, searchValue, getFormattedResults]);

  const onClearSearch = useCallback(() => {
    setSearchValue('');
  }, []);

  const updateSearchValue = debounce((e) => {
    setSearchValue(e.target.value);
  }, 300);

  const onSelectSection = async (
    setFieldValue: (name: string, value?: number) => void,
    item: { label: string; value: number },
  ) => {
    if (!item.value && item.label) {
      setLoading(true);
      const resource = await create({
        body: { name: item.label },
        invalidates: {
          [ENDPOINTS.contentFolder]: true,
          [ENDPOINTS.contentDashboard]: true,
        },
        onSuccessNotification: {
          message: `Section created successfully`,
        },
        onErrorNotification: {
          message: `Something went wrong while creating the section`,
        },
      });
      setLoading(false);
      setFieldValue('folder', resource.id);
      return;
    }

    setFieldValue('folder', item?.value);
  };

  return (
    <Formik
      validateOnMount
      initialValues={{
        contentName: contentTitle || '',
        folder: Number.isNaN(Number(folderId)) ? undefined : Number(folderId),
      }}
      validationSchema={validationSchema}
      onSubmit={({ contentName, folder }) =>
        onSubmit({ name: contentName, folderId: folder, contentType })
      }
    >
      {({ isSubmitting, values, isValid, setFieldValue }) => (
        <Form data-cy="create-content-form">
          <Field name="contentName">
            {({
              field,
              meta,
            }: {
              field: FieldInputProps<string>;
              meta: FieldMetaProps<string>;
            }) => (
              <FormField
                label="Name"
                necessity="required"
                feedback={meta.touched ? meta.error : ''}
                state={meta.touched && meta.error ? 'danger' : 'none'}
                labelScale="small"
              >
                <TextInput {...field} {...meta} placeholder={namePlaceholderTxt[contentType]} />
              </FormField>
            )}
          </Field>
          <Field name="folder">
            {({
              field,
              meta,
            }: {
              field: FieldInputProps<string>;
              meta: FieldMetaProps<string>;
            }) => (
              <FormField label="Content section" labelScale="small">
                <SelectInput<Item>
                  {...field}
                  {...meta}
                  searchable
                  scale="small"
                  clearable={false}
                  items={sectionOptions}
                  loading={isFetching || loading}
                  onFilter={updateSearchValue}
                  onClearFilter={onClearSearch}
                  renderItem={renderSectionItem}
                  onOpeningChange={onClearSearch}
                  selectedItem={sectionOptions.find((option) => option.value === values.folder)}
                  onSelect={(selected) => selected && onSelectSection(setFieldValue, selected)}
                />
              </FormField>
            )}
          </Field>

          <Stack justify="center">
            <Button
              type="submit"
              variant="secondary"
              mt="s"
              loading={isSubmitting}
              disabled={!isValid}
            >
              {withSnapshots && contentType !== ContentType.Inline ? 'Next' : 'Create'}
            </Button>
          </Stack>
        </Form>
      )}
    </Formik>
  );
};
